import { ReactNode, Suspense, useCallback, useMemo } from "react";
import { trim } from "lodash";
import { BlockId } from "blocks/types";
import { DateFormatOptions, PhoneNumberFormatOptions } from "../schema";
import type { FieldBlock as FieldBlockType } from "../types";
import {
  Field,
  TextInput,
  TextArea,
  YesNoInput,
  UrlInput,
  DatePicker,
  DropdownSelect,
  PhoneNumberInput,
  Checkbox,
  FileUpload,
  Currency,
  DynamicDropdownSelect,
  BlockWrapper,
} from "blocks/components";
import { buildValidationRules } from "./utils";
import { Controller, useFormContext } from "react-hook-form";
import { Spinner } from "blocks/components/Spinner";
import ReactMarkdown from "react-markdown";
import { useContentDatasource } from "lib/hooks/useContentDatasource";
import { isDynamicVariable } from "../utils";
import { parseHandlebars } from "lib/utils/parseHandlebars";
import { usePageContext } from "lib/context/page-context";
import { isAdvanced, isLegacy, isPremium } from "lib/utils/stripe";

const MEGABYTE = 1024 * 1024;

export const FREE_FILE_SIZE = 5 * MEGABYTE;
export const PREMIUM_FILE_SIZE = 10 * MEGABYTE;
export const ADVANCED_FILE_SIZE = 25 * MEGABYTE;

export function FieldBlock(props: { block: FieldBlockType; blockId: BlockId }) {
  const { config } = props.block;
  const { data: datasource } = useContentDatasource();
  const { isEditing, creator } = usePageContext();

  const name = config.name;

  const validationRules = buildValidationRules(config);

  const {
    formState: { errors, isSubmitting },
  } = useFormContext();

  const renderMarkdown = useCallback(
    (content: string) => {
      if (!isEditing) content = parseHandlebars(content, datasource);

      return (
        <ReactMarkdown
          allowedElements={[
            "a",
            "p",
            "strong",
            "em",
            "code",
            "pre",
            "li",
            "ol",
            "ul",
          ]}
          linkTarget="_blank"
        >
          {content}
        </ReactMarkdown>
      );
    },
    [datasource, isEditing]
  );

  const maxFileSize = useMemo(() => {
    if (!creator) return FREE_FILE_SIZE;
    if (isLegacy(creator.planId) || isPremium(creator.planId))
      return PREMIUM_FILE_SIZE;
    if (isAdvanced(creator.planId) || creator.isStaff)
      return ADVANCED_FILE_SIZE;

    return FREE_FILE_SIZE;
  }, [creator]);

  const renderContent = () => {
    const renderInput = (inputProps: any, controllerProps: any): ReactNode => {
      if (
        !isEditing &&
        config.placeholder &&
        isDynamicVariable(config.placeholder.replace(/\s/g, ""))
      ) {
        config.placeholder = parseHandlebars(config.placeholder, datasource);
      }

      switch (config.inputType) {
        case "text":
        case "number":
          return (
            <TextInput
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
              type="text"
            />
          );
        case "email":
          return (
            <TextInput
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
              type={config.inputType}
            />
          );
        case "url":
          return (
            <UrlInput
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
            />
          );

        case "textarea":
          return (
            <TextArea
              {...inputProps}
              {...controllerProps}
              rows={3}
              placeholder={config.placeholder}
              name={name}
            />
          );

        case "phone-number":
          return (
            <PhoneNumberInput
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
              defaultCountry={config.defaultCountry}
              phoneNumberFormat={
                config.phoneNumberFormat ?? PhoneNumberFormatOptions[0].value
              }
            />
          );

        case "dropdown":
          // make separate DynamicDropdown component that calls Tables API and conditionally render it if this won't be a static dropdown
          // otherwise use static one as below
          if (config.tableId && config.tableFieldId) {
            // return dynamic dropdown
            return (
              <DynamicDropdownSelect
                {...inputProps}
                {...controllerProps}
                placeholder={config.placeholder}
                tableId={config.tableId}
                tableFieldId={config.tableFieldId}
                tablesFieldKey={config.tablesFieldKey}
                name={name}
                multiSelect={config.multiSelect}
              />
            );
          }
          return (
            <DropdownSelect
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
              options={getDropdownOptions(config.options)}
              name={name}
              multiSelect={config.multiSelect}
            />
          );

        case "yes-no":
          return (
            <YesNoInput {...inputProps} {...controllerProps} name={name} />
          );

        case "date-picker":
          return (
            <DatePicker
              {...inputProps}
              {...controllerProps}
              name={name}
              dateFormat={config.dateFormat ?? DateFormatOptions[0].value}
              placeholder={config.placeholder}
              includeTime={config.includeTime}
              defaultToNow={config.defaultToNow}
            />
          );

        case "checkbox":
          return (
            <Checkbox
              {...inputProps}
              {...controllerProps}
              label={config.label}
              name={name}
              required={config.required}
              defaultChecked={config.defaultChecked}
              text={config.text}
            />
          );

        case "currency":
          return (
            <Currency
              {...inputProps}
              {...controllerProps}
              placeholder={config.placeholder}
              currencyFormat={config.currencyFormat}
              decimals={config.decimals}
            />
          );

        case "file-upload":
          const allowedFileSize = FREE_FILE_SIZE;

          // This ensures that we never give away more upload size than allowed by the plan
          if (config.allowedFileSize)
            Math.min(config.allowedFileSize, maxFileSize);

          return (
            <FileUpload
              {...inputProps}
              {...controllerProps}
              label={config.label}
              name={name}
              required={config.required}
              placeholder={config.placeholder}
              allowedFileTypes={config.allowedFileTypes}
              maxFileSize={allowedFileSize}
            />
          );

        default:
          const _exhaustiveCheck: never = config;
          return _exhaustiveCheck;
      }
    };

    return (
      <Suspense fallback={<Spinner height={20} />}>
        <Controller
          key={config.id}
          name={name}
          rules={validationRules}
          render={({ field: controllerProps }) => (
            <Field
              inputId={config.name}
              label={config.label}
              isDisabled={isSubmitting}
              isRequired={config.required}
              renderHelpText={
                config.helpText
                  ? () => renderMarkdown(config.helpText ?? "")
                  : undefined
              }
              error={errors?.[name]?.message as string | undefined}
              renderInput={(inputProps) =>
                renderInput(inputProps, controllerProps)
              }
              hideLabel={!config.label}
            />
          )}
        />
      </Suspense>
    );
  };

  if (config.hidden) return null;

  return (
    <BlockWrapper blockId={props.blockId} block={props.block}>
      {renderContent()}
    </BlockWrapper>
  );
}

export function getDropdownOptions(options?: string) {
  return (
    options
      ?.split("\n")
      .map(trim)
      .filter((option) => !!option)
      .map((option) => ({
        label: option,
        value: option,
      })) ?? []
  );
}
